import {
  useSharedValue,
  withRepeat,
  withTiming,
  useAnimatedStyle,
  SharedValue,
} from "react-native-reanimated";
import { useEffect, useState } from "react";
import LBox from "../LBox";

interface ProgressBarBufferingProps {
  songId?: string;
  duration: number;
  progressValue: SharedValue<number>;
}

export const ProgressBarBuffering = ({
  songId,
  duration,
  progressValue,
}: ProgressBarBufferingProps) => {
  const widthAnimation = useSharedValue(0);
  const opacityAnimation = useSharedValue(0.9);
  const [visible, setVisible] = useState(false);
  const [prevSongId, setPrevSongId] = useState(songId);

  // Reset visibility during render when the song changes, instead of in an
  // effect, so the bar is hidden again before the 1s delay timer re-runs.
  if (songId !== prevSongId) {
    setPrevSongId(songId);
    setVisible(false);
  }

  useEffect(() => {
    widthAnimation.value = 0;
    opacityAnimation.value = 0.9;

    const delayTimer = setTimeout(() => {
      setVisible(true);
      widthAnimation.value = withRepeat(
        withTiming(100, { duration: 800 }),
        -1,
        false,
      );
      opacityAnimation.value = withRepeat(
        withTiming(0.1, { duration: 800 }),
        -1,
        false,
      );
    }, 1000);

    return () => clearTimeout(delayTimer);
  }, [songId]);

  const animatedStyle = useAnimatedStyle(() => ({
    width: `${widthAnimation.value}%`,
    opacity: opacityAnimation.value,
  }));

  // Start the animation where playback has reached, so the part already
  // played stays visible and only the remaining part shows as loading.
  const remainingStyle = useAnimatedStyle(() => {
    const percent = duration > 0 ? (progressValue.value / duration) * 100 : 0;
    return { marginLeft: `${Math.max(0, Math.min(100, percent))}%` };
  }, [duration]);

  if (!visible) return null;

  return (
    <LBox
      height={4}
      borderRadius="m"
      justifyContent="center"
      backgroundColor="progressTrackBg"
      style={remainingStyle}
    >
      <LBox
        height={3}
        borderRadius="m"
        backgroundColor="white"
        style={animatedStyle}
      />
    </LBox>
  );
};
